import React from "react";
import { Link } from "react-router-dom";

const TEMPLATES = [
  {
    category: "Strategy",
    items: [
      "AI Use Case Prioritization Matrix",
      "90-Day AI Execution Roadmap",
      "Stakeholder Alignment Brief",
    ],
  },
  {
    category: "Operations",
    items: [
      "Automation Opportunity Backlog",
      "Workflow QA Checklist",
      "Implementation Risk Register",
    ],
  },
  {
    category: "Marketing",
    items: [
      "Campaign Brief Generator",
      "SEO Content Planning Sheet",
      "Audience Message Framework",
    ],
  },
  {
    category: "Product & Engineering",
    items: [
      "AI Feature Discovery Canvas",
      "Prompt Design Spec",
      "Release Readiness Checklist",
    ],
  },
];

export default function TemplatesLibrary() {
  return (
    <main className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-teal-900 to-emerald-900 text-white px-4 py-20">
        <div className="max-w-6xl mx-auto">
          <p className="text-xs uppercase tracking-[0.24em] text-emerald-300 font-bold">Templates Library</p>
          <h1 className="text-4xl sm:text-5xl font-black mt-3 max-w-4xl">Reusable templates that help teams execute faster and more consistently.</h1>
          <p className="text-lg text-slate-300 max-w-3xl mt-5">
            Use these frameworks to structure planning, implementation, and review cycles across AI, product, and growth initiatives.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 gap-6">
          {TEMPLATES.map((group) => (
            <div key={group.category} className="border border-gray-200 rounded-2xl p-6">
              <h2 className="text-xl font-black text-slate-900 mb-4">{group.category}</h2>
              <ul className="space-y-2">
                {group.items.map((item) => (
                  <li key={item} className="text-sm text-slate-600 flex items-start gap-2">
                    <span className="text-emerald-600 font-bold">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <h2 className="text-3xl font-black text-slate-900 mb-4">How to use templates effectively</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-8">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-bold text-slate-900 mb-2">Adapt, do not copy</h3>
              <p className="text-sm text-slate-600">Start from the template, then tailor assumptions, KPI definitions, and ownership to your team.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-bold text-slate-900 mb-2">Reuse language patterns</h3>
              <p className="text-sm text-slate-600">Consistent language in briefs and plans reduces misalignment across product, engineering, and business teams.</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-bold text-slate-900 mb-2">Review weekly</h3>
              <p className="text-sm text-slate-600">Treat each template as a live artifact and update it with real execution learnings every week.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-16">
        <div className="rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-700 text-white p-8 sm:p-10">
          <h2 className="text-3xl font-black mb-3">Pair templates with playbooks</h2>
          <p className="text-emerald-100 mb-6 max-w-2xl">
            Templates give structure. Playbooks give sequence. Use both for better execution speed and quality.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/playbooks" className="px-5 py-2.5 rounded-lg bg-white text-emerald-700 font-bold hover:bg-emerald-50 transition">Go to Playbooks</Link>
            <Link to="/insights" className="px-5 py-2.5 rounded-lg border border-white/40 text-white font-semibold hover:bg-white/10 transition">Back to Insights Hub</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
